// Base
import { useState } from 'react'

// Redux
import { useAppDispatch, useAppSelector } from '@redux/hooks'
import {
  hideErrorMessage,
  showErrorMessage
} from '@redux/slices/utilitiesSlice'

// Helpers
import { publicFetch } from '@helpers/contentAPI'

// Components
import Input from '@components/inputs/Input'
import Box from '@components/helpers/box/Box'
import Text from '@components/helpers/text/Text'
import Flex from '@components/helpers/flex/Flex'
import SearchButton from './SearchButton'
import LoadingSpinner from '@components/helpers/loadingSpinner/LoadingSpinner'

// Styles
import styles from './SelectionBox.module.sass'

// Icons
import { IoCloseCircleOutline } from 'react-icons/io5'

// Interfaces
import { SelectedData } from '@sections/location/LocationWrapper'

interface SelectionProps {
  endpoint: string
  placeholder: string
  inputIcon?: JSX.Element
  listIcon?: JSX.Element
  multiple?: boolean
  disabled?: boolean
  setSelected: Function
}

const Selection = ({
  endpoint,
  placeholder,
  inputIcon,
  listIcon,
  multiple = false,
  disabled = false,
  setSelected
}: SelectionProps) => {
  const [search, setSearch] = useState('')
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout>()
  const [searchLoading, setSearchLoading] = useState(false)
  const [showList, setShowList] = useState(false)
  const [selection, setSelection] = useState<SelectedData[]>([])

  const dispatch = useAppDispatch()
  const { errorMessage } = useAppSelector((state) => state.utilities)

  const inputEndIcon = () => {
    if (searchLoading) {
      return <LoadingSpinner size="sm" color="black" />
    }

    if (search) {
      return (
        <Flex
          className={styles.selection__input__close}
          onClick={() => {
            setSearch('')
          }}
        >
          <IoCloseCircleOutline size={24} color="#959DA1" />
        </Flex>
      )
    }
    if (!search) {
      return <SearchButton />
    }
    return ''
  }

  const searchInput = async (value: string) => {
    if (value) {
      setSearchLoading(true)
      clearTimeout(searchTimeout)

      const timeout = setTimeout(async () => {
        if (value.length < 3) {
          setShowList(false)
          dispatch(showErrorMessage('Write more than 3 letters'))
          setSearchLoading(false)
          return
        }

        const fetchedSelection = await publicFetch({
          url: endpoint,
          params: {
            _q: value
          }
        })

        if (fetchedSelection.data.length == 0) {
          dispatch(
            showErrorMessage(
              'Please enter a valid airport and select from the list below.'
            )
          )
        } else {
          dispatch(hideErrorMessage())
        }

        setSelection(fetchedSelection.data)

        setShowList(true)
        setSearchLoading(false)
      }, 500)

      setSearchTimeout(timeout)
    } else {
      setShowList(false)
      setSearchLoading(false)
      clearTimeout(searchTimeout)
    }
  }

  const selectSingleOption = (airport: string, id: number) => {
    setSearch(airport)
    setSelected({ airport, id })
    setShowList(false)
  }

  const selectMultipleOptions = (city: string, id: number) => {
    setSearch('')
    setSelected((prevState: SelectedData[]) => {
      if (prevState.find((value) => value.id == id)) {
        return [...prevState]
      } else {
        return [
          ...prevState,
          {
            name: city,
            id
          }
        ]
      }
    })
    setShowList(false)
  }

  const updateSearch = (value: string) => {
    setSearch(value)
    searchInput(value)
  }

  return (
    <div className={styles.selection}>
      <div className={styles.selection__input}>
        <Input
          type="text"
          name="selection"
          placeholder={placeholder}
          value={search}
          autoComplete="off"
          onChange={updateSearch}
          endIcon={inputEndIcon()}
          startIcon={inputIcon}
          error={errorMessage}
          disabled={disabled}
        />
      </div>

      {selection.length > 0 && search && showList && !disabled && (
        <Box
          backgroundColor="washedWhite"
          padding={{ sm: [64, 16, 16, 16] }}
          borderRadius={{ sm: [8] }}
          shadow="sm"
          className={styles.selection__box}
        >
          {selection &&
            selection.map((item: SelectedData, index: number) => (
              <Flex
                alignItems="center"
                columnGap={{ sm: 8 }}
                key={index}
                className={styles.selection__box__item}
                onClick={() => {
                  multiple
                    ? selectMultipleOptions(item.name, item.id)
                    : selectSingleOption(item.name, item.id)
                }}
              >
                {listIcon}
                <Text
                  tagName="p"
                  variant="body-1"
                  color="grey700"
                  className={styles.selection__box__item__text}
                >
                  {item.name}
                </Text>
              </Flex>
            ))}
        </Box>
      )}
    </div>
  )
}

export default Selection
